import { useState, useEffect, useCallback } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import GraphingAdditionalMetaEditor from './components/GraphingAdditionalMetaEditor';
import GraphingAxisEditor from './components/GraphingAxisEditor';
import GraphingPointsEditor from './components/GraphingPointsEditor';
import GraphingLinesEditor from './components/GraphingLinesEditor';
import { GraphingRenderer } from '@/src/lib';

import {
    ControlledDynamicTextEditor,
    isEditorContentEmpty,
    type CDTEFieldType,
    type QtiGraphingQuestionDTO,
} from '@/src/lib';

import {
    Debugger,
    QuestionMetaForm,
    TextEntryModal,
    QuestionTitleField,
    type TextEntryModalPartialProps,
} from '../_shared';

import { metaSchema } from '../../models/schema';
import { Constants } from '@/src/utils';
import { defaultMeta } from '../../models/questionDTOs';

import { ErrorMessage, SimpleButton } from '@/src/ui';
import { renderLoaders } from '../../utils';
import { saveQuestion } from '../../services';

const qtm = Constants.QUESTION_TYPE_MASTER_IDS;

const graphSchema = yup.object({
    id: yup.number().optional(),
    title: yup
        .string()
        .trim()
        .required('Title is required')
        .max(
            Constants.MAX_CHARS.TITLE,
            `Title cannot exceed ${Constants.MAX_CHARS.TITLE} characters`
        ),
    prompt: yup
        .string()
        .test('not-empty', 'Prompt cannot be empty', (value) => !isEditorContentEmpty(value || '')),
    prompt_es: yup.string().when('$isSpanishAllowed', {
        is: true,
        then: (schema) =>
            schema
                .test(
                    'not-empty',
                    'Prompt (Spanish) cannot be empty',
                    (value) => !isEditorContentEmpty(value || '')
                )
                .required('Prompt (Spanish) is required'),
        otherwise: (schema) => schema.optional(),
    }),
    axis: yup
        .object({
            minX: yup.number().typeError('minX must be a number').required('minX is required'),
            maxX: yup.number().typeError('maxX must be a number').required('maxX is required'),
            minY: yup.number().typeError('minY must be a number').required('minY is required'),
            maxY: yup.number().typeError('maxY must be a number').required('maxY is required'),

            snapX: yup
                .number()
                .typeError('snapX must be a number')
                .moreThan(0, 'snapX must be > 0')
                .required('snapX is required'),
            snapY: yup
                .number()
                .typeError('snapY must be a number')
                .moreThan(0, 'snapY must be > 0')
                .required('snapY is required'),
        })
        .required('Axis config is required')
        .test('ranges-ok', 'maxX must be > minX and maxY must be > minY', (axis) => {
            if (!axis) return false;
            return axis.maxX > axis.minX && axis.maxY > axis.minY;
        }),
    points: yup
        .array()
        .of(
            yup.object({
                id: yup.string().trim().required('Point ID is required'),
                label: yup.string().trim().required('Point label is required'),
                x: yup.number().typeError('x must be a number').required('x is required'),
                y: yup.number().typeError('y must be a number').required('y is required'),
            })
        )
        .required()
        .max(30, 'Too many points'),
    lines: yup
        .array()
        .of(
            yup.object({
                id: yup.string().trim().required('Line ID is required'),
                label: yup.string().trim().optional(),
                style: yup
                    .mixed<'segment' | 'ray' | 'line'>()
                    .oneOf(['segment', 'ray', 'line'])
                    .required('Line style is required'),
                x1: yup.number().typeError('x1 must be a number').required('x1 is required'),
                y1: yup.number().typeError('y1 must be a number').required('y1 is required'),
                x2: yup.number().typeError('x2 must be a number').required('x2 is required'),
                y2: yup.number().typeError('y2 must be a number').required('y2 is required'),
                color: yup.string().optional(),
            })
        )
        .required()
        .max(30, 'Too many lines'),
    additionalMeta: yup
        .object({
            allowPoints: yup.boolean().required(),
            allowSegments: yup.boolean().required(),
            allowRays: yup.boolean().required(),
            allowLines: yup.boolean().required(),
        })
        .required(),
    meta: metaSchema.required(),
});

const defaultAxis = {
    minX: -10,
    maxX: 10,
    minY: -10,
    maxY: 10,
    snapX: 1,
    snapY: 1,
};

const lineTypes = ['Segment', 'Ray', 'Line'].map((type) => ({
    id: type.toLowerCase(),
    name: type,
}));

interface GraphingEditorProps {
    initialModel?: QtiGraphingQuestionDTO;
}

export default function GraphingEditor({ initialModel }: GraphingEditorProps) {
    const isEdit = !!initialModel;

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isTitleError, setIsTitleError] = useState(false);

    const [modalProps, setModalProps] = useState<TextEntryModalPartialProps | null>(null);

    const defaultValues: QtiGraphingQuestionDTO = initialModel
        ? initialModel
        : {
              title: '',
              prompt: '',
              prompt_es: '',
              axis: { ...defaultAxis },
              points: [],
              lines: [],
              additionalMeta: {
                  allowPoints: true,
                  allowSegments: true,
                  allowRays: true,
                  allowLines: true,
              },
              meta: {
                  ...defaultMeta,
                  questionTypeMasterId: qtm.GRAPHING,
              },
          };

    const [formContext, setFormContext] = useState({
        isSpanishAllowed: defaultValues.meta.isSpanishAllowed,
    });

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isSubmitting },
        setError: setFormError,
        clearErrors,
    } = useForm<QtiGraphingQuestionDTO>({
        defaultValues,
        resolver: yupResolver(graphSchema as any),
        mode: 'onBlur',
        context: formContext,
    });

    const question = watch();
    useEffect(() => {
        setFormContext({
            isSpanishAllowed: question.meta.isSpanishAllowed,
        });
    }, [question.meta.isSpanishAllowed]);

    const handleLanguageSet = useCallback(
        (language: 'en' | 'es') => {
            if (language === 'en') return;

            if (!question.prompt_es) {
                setValue('prompt_es', question.prompt);
            }
        },
        [question, setValue]
    );

    const onSubmit = async () => {
        if (success) return;
        if (isTitleError) {
            setError('Please choose a unique title.');
            return;
        }

        clearErrors();
        setLoading(true);
        setError(null);
        try {
            const viewMode = isEdit ? 'edit' : 'create';
            await saveQuestion(question, viewMode);
            setSuccess(true);
        } catch (e: any) {
            setError(e.message || 'Unknown error');
            setFormError('root', {
                type: 'manual',
                message: e.message || 'Unknown error',
            });
        } finally {
            setLoading(false);
        }
    };

    const allowedLineTypes = (() => {
        const am = question.additionalMeta ?? {};
        const styles: Array<'segment' | 'ray' | 'line'> = [];
        if (am.allowSegments) styles.push('segment');
        if (am.allowRays) styles.push('ray');
        if (am.allowLines) styles.push('line');
        return styles;
    })();

    return (
        <>
            <form
                key={`GraphingEditor-${question.meta.isSpanishAllowed}`}
                className="p-4 space-y-4"
                onSubmit={handleSubmit(onSubmit)}
            >
                {/* Handles all Spanish entry */}
                {modalProps && (
                    <TextEntryModal
                        initialData={modalProps.initialData}
                        fieldName={modalProps.fieldName}
                        friendlyName={modalProps.friendlyName}
                        textMode={modalProps.textMode}
                        error={modalProps.error}
                        control={control}
                        handleClose={() => setModalProps(null)}
                    />
                )}

                {renderLoaders(loading, success, error, isEdit)}

                <h2>Graphing Question Title:</h2>
                <QuestionTitleField
                    id={question.id}
                    title={question.title}
                    control={control}
                    setIsTitleError={setIsTitleError}
                    error={errors.title}
                />

                <div className="my-4" />

                <div className="flex flex-row gap-4 items-start">
                    <h2>Prompt:</h2>

                    {question.meta.isSpanishAllowed && (
                        <div
                            onClick={() =>
                                setModalProps({
                                    initialData: question.prompt_es,
                                    fieldName: 'prompt_es',
                                    friendlyName: 'Prompt (Spanish)',
                                    textMode: 'rich',
                                    error: errors.prompt_es?.message,
                                })
                            }
                            style={{ cursor: 'pointer' }}
                        >
                            Edit Spanish
                        </div>
                    )}

                    <ErrorMessage error={errors.prompt_es} />
                </div>

                <ControlledDynamicTextEditor
                    name="prompt"
                    control={control}
                    initialValue={question.prompt}
                    setOutput={(val: string, field: CDTEFieldType) => {
                        field.onChange(val);
                        setValue('prompt', val, { shouldValidate: true });
                    }}
                    style={{ minHeight: '150px' }}
                />
                <ErrorMessage error={errors.prompt} />

                <GraphingAdditionalMetaEditor
                    control={control}
                    allDisabled={
                        question.points.length === 0 && question.lines.length === 0 ? false : true
                    }
                    disableList={[]}
                />

                <div className="my-8" />

                <h2>Graph Axes / Grid:</h2>
                <GraphingAxisEditor control={control} errors={errors} />

                <div className="my-8" />

                {question.additionalMeta?.allowPoints && (
                    <>
                        <div className="my-4" />
                        <h2>Points:</h2>
                        <GraphingPointsEditor
                            question={question}
                            control={control}
                            errors={errors}
                        />
                    </>
                )}

                <div className="my-8" />

                {allowedLineTypes.length > 0 && (
                    <>
                        <div className="my-4" />
                        <h2>Lines:</h2>
                        <GraphingLinesEditor
                            question={question}
                            control={control}
                            errors={errors}
                            lineOptions={
                                lineTypes.filter((o) =>
                                    allowedLineTypes.some((s) => s === o.id)
                                ) as { id: 'segment' | 'ray' | 'line'; name: string }[]
                            }
                        />
                    </>
                )}

                <div className="my-8" />

                <QuestionMetaForm
                    rhfProps={{
                        control,
                        errors: errors.meta,
                        watch,
                    }}
                    handleLanguageSet={handleLanguageSet}
                    disableList={['shouldShuffleOptions', 'shouldShowKeypad']}
                    metaValues={watch('meta')}
                    setMetaValues={(meta) => setValue('meta', meta)}
                />

                <div className="border rounded p-4 bg-white max-w-4xl space-y-4 mt-4">
                    <h4 className="font-semibold mb-1">Interactive Preview</h4>
                    <GraphingRenderer
                        question={question}
                        options={{
                            showLanguageToggle: question.meta.isSpanishAllowed,
                            showScoring: true,
                        }}
                    />
                </div>

                <div className="flex items-center space-x-2">
                    <div>
                        <SimpleButton
                            type="submit"
                            color="primary"
                            className="bg-green-600 text-white px-3 py-1 rounded text-xs"
                            disabled={isSubmitting || loading}
                        >
                            {isEdit ? 'Update Question' : 'Create Question'}
                        </SimpleButton>
                    </div>
                </div>

                <ErrorMessage
                    error={
                        Object.keys(errors).length ? 'Please correct the errors to continue' : ''
                    }
                />

                {renderLoaders(loading, success, error, isEdit)}

                <Debugger question={question} errors={errors} />
            </form>
        </>
    );
}
